import {
  Calendar,
  DeliveryBike,
  DeliveryCar,
  DeliveryMotorcycle,
  EyeFilled,
  List,
  MoneyBags,
  Trashbin,
  WarehousePackages,
  CalendarClock,
} from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { OptionsIcon } from 'modules/menu/items/components/OptionBinding';

import ActionButton from './ActionButton';
import { BulkActionModalType, BulkActionType } from './BulkActionsToolbar';
import { MandatoryWarningIcon } from './BulkMandatoryWarnings';
import { CoinStackIcon } from './BulkPriceChange';
import { VatIcon } from './BulkVatChange';
import WoltSpecialIcon from './CreateSpecials/WoltSpecialIcon';

type Props = {
  disableBulkCategoryChange: boolean;
  disableBulkCreateSpecials: boolean;
  disableBulkAddOptions: boolean;
  disableBulkMarkInStockOption: boolean;
  disableBulkMarkOutOfStockOption: boolean;
  disableBulkUpdateSpecials: boolean;
  setAction: (action: BulkActionType) => void;
  setShownModal: (modal: BulkActionModalType) => void;
};

const Root = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  margin-inline-start: 16px;

  svg {
    fill: ${cbSemanticCssVars.textPrimary};
  }
`;

const Divider = styled.span`
  height: 36px;
  width: 1px;
  background-color: ${cbSemanticCssVars.borderPrimary};
  margin-inline-end: 16px;
`;

const OptionPopover = styled(Popover).attrs({
  placement: 'bottom-start',
  showOnHover: false,
  showOnClick: false,
  renderArrow: false,
})`
  margin-inline-end: 16px;
`;

const OptionPopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 240px;
`;

const Action = styled(MenuActionItem)`
  & + & {
    margin-block-start: 8px;
  }
`;

const BulkActionButtons = ({
  disableBulkCategoryChange,
  disableBulkCreateSpecials,
  disableBulkUpdateSpecials,
  disableBulkAddOptions,
  disableBulkMarkInStockOption,
  disableBulkMarkOutOfStockOption,
  setAction,
  setShownModal,
}: Props) => {
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const [showMandatoryWarningOptions, setShowMandatoryWarningOptions] = useState(false);
  const [showCourierRestrictionOptions, setShowCourierRestrictionOptions] = useState(false);
  const [showChangeStockAvailabilityOptions, setShowChangeStockAvailabilityOptions] = useState(false);
  const menuPermissions = useContext(MenuPermissionsContext);
  const menuFeatures = useContext(MenuFeaturesContext);

  return (
    <Root>
      <OptionPopover
        content={
          <OptionPopoverContent>
            <Action variant="black" onClick={() => setShownModal('enable')}>
              <FormattedMessage id="menu.bulkActions.statusChange.enableItems" />
            </Action>
            <Action variant="red" onClick={() => setShownModal('disable')}>
              <FormattedMessage id="menu.bulkActions.statusChange.disableItems" />
            </Action>
          </OptionPopoverContent>
        }
        onClickInside={() => setShowStatusOptions(false)}
        onClickOutside={() => setShowStatusOptions(false)}
        show={showStatusOptions}
      >
        <ActionButton
          disableEndMargin
          icon={<EyeFilled />}
          onClick={() => setShowStatusOptions(!showStatusOptions)}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.statusChange.enableOrDisable" />}
        />
      </OptionPopover>

      <ActionButton
        disabled={!menuPermissions.contentEdit}
        icon={<Calendar />}
        onClick={() => setShownModal('changeAvailabilityAndVisibility')}
        tooltipTitle={<FormattedMessage id="menu.bulkActions.availabilityAndVisibilityChange.title" />}
      />

      <OptionPopover
        show={showChangeStockAvailabilityOptions}
        onClickOutside={() => setShowChangeStockAvailabilityOptions(false)}
        onClickInside={() => setShowChangeStockAvailabilityOptions(false)}
        content={
          <OptionPopoverContent>
            <Action
              variant="black"
              disabled={disableBulkMarkOutOfStockOption}
              onClick={() => setAction('bulkMarkOutOfStock')}
            >
              <FormattedMessage id="menu.bulkActions.stock.markOutOfStock" />
            </Action>
            <Action
              variant="black"
              disabled={disableBulkMarkInStockOption}
              onClick={() => setAction('bulkMarkInStock')}
            >
              <FormattedMessage id="menu.bulkActions.stock.markInStock" />
            </Action>
          </OptionPopoverContent>
        }
      >
        <ActionButton
          disableEndMargin
          disabled={disableBulkMarkOutOfStockOption && disableBulkMarkInStockOption}
          icon={<WarehousePackages />}
          onClick={() => setShowChangeStockAvailabilityOptions(state => !state)}
          tooltipTitle={
            disableBulkMarkOutOfStockOption && disableBulkMarkInStockOption ? (
              <FormattedMessage id="menu.bulkActions.stock.disabledText" />
            ) : (
              <FormattedMessage id="menu.bulkActions.stock.changeAvailability" />
            )
          }
        />
      </OptionPopover>

      <ActionButton
        disabled={!menuPermissions.contentEdit}
        icon={<CoinStackIcon />}
        onClick={() => setShownModal('priceChange')}
        tooltipTitle={<FormattedMessage id="menu.bulkActions.priceChange.changePrice" />}
      />

      <OptionPopover
        content={
          <OptionPopoverContent>
            <Action icon={<DeliveryBike />} variant="black" onClick={() => setShownModal('disableBikeDelivery')}>
              <FormattedMessage id="menu.bulkActions.courierRestrictions.disableBikeDelivery" />
            </Action>
            <Action icon={<DeliveryCar />} variant="black" onClick={() => setShownModal('disableCarDelivery')}>
              <FormattedMessage id="menu.bulkActions.courierRestrictions.disableCarDelivery" />
            </Action>
            <Action
              icon={<DeliveryMotorcycle />}
              variant="black"
              onClick={() => setShownModal('disableMotorcycleDelivery')}
            >
              <FormattedMessage id="menu.bulkActions.courierRestrictions.disableMotorcycleDelivery" />
            </Action>
          </OptionPopoverContent>
        }
        onClickInside={() => setShowCourierRestrictionOptions(false)}
        onClickOutside={() => setShowCourierRestrictionOptions(false)}
        show={showCourierRestrictionOptions}
      >
        <ActionButton
          disableEndMargin
          disabled={!menuPermissions.contentEdit}
          icon={<DeliveryBike />}
          onClick={() => setShowCourierRestrictionOptions(!showCourierRestrictionOptions)}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.courierRestrictions.title" />}
        />
      </OptionPopover>

      <ActionButton
        disabled={!menuPermissions.contentEdit || disableBulkAddOptions}
        icon={<OptionsIcon />}
        onClick={() => setShownModal('addOptions')}
        tooltipTitle={
          <FormattedMessage
            id={
              disableBulkAddOptions
                ? 'menu.bulkActions.addOptionsToItems.disabledLabel'
                : 'menu.bulkActions.addOptionsToItems.addOptions'
            }
          />
        }
      />

      <ActionButton
        disabled={!menuPermissions.contentEdit || disableBulkCategoryChange}
        icon={<List />}
        onClick={() => setShownModal('changeCategory')}
        tooltipTitle={
          disableBulkCategoryChange ? (
            <FormattedMessage id="menu.bulkActions.categoryChange.disabledLabel" />
          ) : (
            <FormattedMessage id="menu.bulkActions.categoryChange.title" />
          )
        }
      />
      {menuPermissions.customCommissionEdit && (
        <ActionButton
          disabled={!menuPermissions.contentEdit || !menuPermissions.customCommissionEdit}
          icon={<MoneyBags />}
          iconButtonVariant="black"
          onClick={() => setShownModal('changeCommissions')}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.commissionsChange.title" />}
        />
      )}

      {menuPermissions.itemVatChange && (
        <ActionButton
          disabled={!menuPermissions.contentEdit || !menuPermissions.itemVatChange}
          icon={<VatIcon />}
          onClick={() => setShownModal('changeVat')}
          tooltipTitle={<FormattedMessage id="menu.bulkActions.vatChange.title" />}
        />
      )}

      {menuFeatures.mandatoryWarnings && (
        <OptionPopover
          content={
            <OptionPopoverContent>
              <Action variant="black" onClick={() => setShownModal('addMandatoryWarnings')}>
                <FormattedMessage id="menu.bulkActions.mandatoryWarnings.addMandatoryWarnings" />
              </Action>
              <Action variant="black" onClick={() => setShownModal('removeMandatoryWarnings')}>
                <FormattedMessage id="menu.bulkActions.mandatoryWarnings.removeMandatoryWarnings" />
              </Action>
            </OptionPopoverContent>
          }
          onClickInside={() => setShowMandatoryWarningOptions(false)}
          onClickOutside={() => setShowMandatoryWarningOptions(false)}
          show={showMandatoryWarningOptions}
        >
          <ActionButton
            disableEndMargin
            disabled={!menuPermissions.contentEdit}
            icon={<MandatoryWarningIcon />}
            onClick={() => setShowMandatoryWarningOptions(oldValue => !oldValue)}
            tooltipTitle={<FormattedMessage id="menu.mandatoryWarnings.title" />}
          />
        </OptionPopover>
      )}

      {menuPermissions.woltSpecials && (
        <>
          <Divider />
          <ActionButton
            disabled={disableBulkCreateSpecials}
            icon={<WoltSpecialIcon />}
            onClick={() => setShownModal('createSpecials')}
            tooltipTitle={
              <FormattedMessage
                id={
                  disableBulkCreateSpecials
                    ? 'menu.bulkActions.createWoltSpecials.disabledLabel'
                    : 'menu.bulkActions.createWoltSpecials.header'
                }
              />
            }
          />
          <ActionButton
            disabled={disableBulkUpdateSpecials}
            icon={<CalendarClock />}
            onClick={() => setShownModal('updateSpecialsPeriod')}
            tooltipTitle={
              <FormattedMessage
                id={
                  disableBulkUpdateSpecials
                    ? 'menu.bulkActions.updateWoltSpecials.disabledLabel'
                    : 'menu.bulkActions.updateWoltSpecials.header'
                }
              />
            }
          />
        </>
      )}

      <Divider />

      <ActionButton
        disabled={!menuPermissions.contentEdit}
        disableEndMargin
        icon={<Trashbin />}
        iconButtonVariant="red"
        onClick={() => setShownModal('bulkDelete')}
        tooltipTitle={<FormattedMessage id="menu.bulkActions.deleteItems" />}
      />
    </Root>
  );
};

export default BulkActionButtons;
